import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Megaphone, Users, CalendarCheck, BadgeIndianRupee, TrendingUp, Target, Wallet, Lightbulb, ArrowRight } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";
import { api, inr, num } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Kpi, PageHeader, CHART, tooltipStyle, Badge2 } from "@/components/common";
import SaleBanner from "@/components/SaleBanner";

export default function Dashboard() {
  const { me } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-slate-500" data-testid="dashboard-loading">Loading dashboard...</div>;
  const k = d.kpis;
  const stages = ["New", "Contacted", "Interested", "Site Visit", "Negotiation", "Booked", "Lost"].map((s) => ({ stage: s, leads: d.funnel[s] || 0 }));
  const recs = d.advisor?.data?.recommendations || [];
  return (
    <div data-testid="dashboard-page">
      <PageHeader label="Executive Command Center" title={`Good day, ${me.user.name.split(" ")[0]}`} subtitle="Your live sales & marketing pulse across every project." />
      <SaleBanner placement="dashboard" />
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        <Kpi testid="kpi-active-projects" label="Active Projects" value={k.active_projects} icon={Building2} />
        <Kpi testid="kpi-active-campaigns" label="Active Campaigns" value={k.active_campaigns} icon={Megaphone} accent="text-cyan-400" />
        <Kpi testid="kpi-leads" label="Leads" value={num(k.leads)} icon={Users} accent="text-cyan-400" />
        <Kpi testid="kpi-site-visits" label="Site Visits" value={num(k.site_visits)} icon={CalendarCheck} accent="text-violet-400" />
        <Kpi testid="kpi-bookings" label="Bookings" value={num(k.bookings)} icon={BadgeIndianRupee} accent="text-amber-400" />
        <Kpi testid="kpi-revenue" label="Revenue" value={inr(k.revenue)} icon={TrendingUp} />
        <Kpi testid="kpi-cost-per-lead" label="Cost / Lead" value={inr(k.cost_per_lead)} icon={Target} accent="text-rose-400" />
        <Kpi testid="kpi-cost-per-booking" label="Cost / Booking" value={inr(k.cost_per_booking)} icon={Target} accent="text-rose-400" />
        <Kpi testid="kpi-total-spend" label="Total Spend" value={inr(k.total_spend)} icon={Wallet} accent="text-amber-400" />
        <Kpi testid="kpi-conversion" label="Lead → Booking" value={`${k.leads ? ((k.bookings / k.leads) * 100).toFixed(1) : 0}%`} icon={TrendingUp} accent="text-cyan-400" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6">
        <div className="lx-card xl:col-span-2" data-testid="dashboard-revenue-chart">
          <div className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Revenue & Leads Trend</div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={d.trend}>
              <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={CHART.revenue} stopOpacity={0.4} /><stop offset="100%" stopColor={CHART.revenue} stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="#e6eaf0" vertical={false} />
              <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
              <YAxis yAxisId="r" stroke="#64748b" fontSize={11} tickFormatter={(v) => inr(v)} width={70} />
              <YAxis yAxisId="l" orientation="right" stroke="#64748b" fontSize={11} />
              <Tooltip {...tooltipStyle} formatter={(v, n) => (n === "revenue" ? inr(v) : v)} />
              <Area yAxisId="r" type="monotone" dataKey="revenue" stroke={CHART.revenue} fill="url(#rev)" strokeWidth={2} />
              <Area yAxisId="l" type="monotone" dataKey="leads" stroke={CHART.leads} fill="transparent" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="lx-card border-cyan-500/20" data-testid="dashboard-ai-recommendations">
          <div className="flex items-center justify-between mb-4">
            <div className="lx-ai-badge"><Lightbulb className="h-3 w-3" />AI Recommendations</div>
            <Link to="/advisor" className="text-xs text-cyan-400 hover:underline flex items-center gap-1" data-testid="dashboard-advisor-link">Advisor <ArrowRight className="h-3 w-3" /></Link>
          </div>
          {d.advisor?.data?.headline && <p className="text-sm text-slate-200 mb-4">{d.advisor.data.headline}</p>}
          <div className="space-y-3">
            {recs.slice(0, 4).map((r, i) => (
              <div key={i} className="rounded-lg border border-slate-800 p-3 hover:border-cyan-500/30 transition-colors">
                <div className="flex items-start justify-between gap-2"><div className="text-sm font-semibold">{r.title}</div><Badge2 tone={r.impact === "High" ? "green" : "amber"}>{r.impact}</Badge2></div>
                <div className="text-xs text-slate-400 mt-1">{r.detail}</div>
              </div>
            ))}
            {!recs.length && <p className="text-sm text-slate-500">Generate your first daily advisor briefing.</p>}
          </div>
        </div>
        <div className="lx-card xl:col-span-2" data-testid="dashboard-funnel-chart">
          <div className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Lead Funnel</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stages}><CartesianGrid stroke="#e6eaf0" vertical={false} /><XAxis dataKey="stage" stroke="#64748b" fontSize={11} /><YAxis stroke="#64748b" fontSize={11} /><Tooltip {...tooltipStyle} /><Bar dataKey="leads" fill={CHART.leads} radius={[6, 6, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
        <div className="lx-card" data-testid="dashboard-sources">
          <div className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Lead Sources</div>
          <div className="space-y-2.5">
            {d.sources.slice(0, 8).map((s) => (
              <div key={s.source}>
                <div className="flex justify-between text-xs mb-1"><span>{s.source}</span><span className="font-mono">{s.leads}</span></div>
                <div className="h-1.5 bg-slate-800 rounded-full"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(s.leads / (d.sources[0]?.leads || 1)) * 100}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
